import { Directive, ElementRef, forwardRef, HostListener, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appToUppercase]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToUppercaseDirective),
      multi: true,
    },
  ],
})
export class ToUppercaseDirective implements ControlValueAccessor {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _onChange = (value: string) => {
    // no-op
  };
  private _onTouched = () => {
    // no-op
  };

  private el = inject(ElementRef);

  private get inputElement(): HTMLInputElement {
    return this.el.nativeElement as HTMLInputElement;
  }

  /** ControlValueAccessor: Write value to input */
  writeValue(value: string): void {
    const input = this.inputElement;
    const formattedValue = value ? value.toUpperCase() : '';

    if (input.value !== formattedValue) {
      const selectionStart = input.selectionStart;
      const selectionEnd = input.selectionEnd;
      input.value = formattedValue;
      if (selectionStart !== null && selectionEnd !== null) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    }
  }

  /** ControlValueAccessor: Register change callback */
  registerOnChange(fn: (_: string) => void): void {
    this._onChange = fn;
  }

  /** ControlValueAccessor: Register touched callback */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** ControlValueAccessor: Handle disabled state */
  setDisabledState(isDisabled: boolean): void {
    this.inputElement.disabled = isDisabled;
  }

  /** Converts value to uppercase on input while preserving the caret position */
  @HostListener('input')
  onInput() {
    const input = this.inputElement;
    const originalValue = input.value;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    const value = originalValue.toUpperCase();

    if (value !== originalValue) {
      input.value = value;
      if (selectionStart !== null && selectionEnd !== null) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    }

    this._onChange(value);
  }

  /** Marks control as touched on blur */
  @HostListener('blur')
  onBlur() {
    this._onTouched();
  }
}
